import React from 'react'
import { Box, Typography, useTheme, alpha } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectCurrentTheme } from '../../selectors/common'
import UpArrow from '../../assets/icons/UpPointer.tsx'

interface RangeBarProps {
    ltp: number
    open: number
    dayLow: number
    dayHigh: number
    weekLow: number
    weekHigh: number
    show52WkRange?: boolean
}

const RangeBar: React.FC<RangeBarProps> = ({ ltp, open, dayLow, dayHigh, weekLow, weekHigh, show52WkRange = true }) => {
    const theme = useTheme()
    const isDarkTheme = useSelector(selectCurrentTheme)

    const getPosition = (value: number, min: number, max: number) => {
        if (max <= min) return 50
        if (isNaN(value) || !isFinite(value)) return 50
        const clampedValue = Math.max(min, Math.min(max, value))
        let pos = ((clampedValue - min) / (max - min)) * 100
        return Math.min(100, Math.max(0, pos))
    }
    const effectiveOpen = open > 0 ? open : ltp
    const ltpPosDay = getPosition(ltp, dayLow, dayHigh)
    const openPosDay = getPosition(effectiveOpen, dayLow, dayHigh)
    const ltpPosWeek = getPosition(ltp, weekLow, weekHigh)

    const dayLowPosWeek = getPosition(dayLow, weekLow, weekHigh)
    const dayHighPosWeek = getPosition(dayHigh, weekLow, weekHigh)

    const isUp = ltp >= effectiveOpen

    // Check if we should show pointers (only if there's a valid range)
    const shouldShowDayPointer = dayHigh > 0 || dayLow > 0
    const shouldShowWeekPointer = weekHigh > 0 || weekLow > 0

    const formatNumber = (val: number) =>
        val.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })

    const barBgColor = isDarkTheme ? alpha(theme.palette.grey[700], 0.5) : theme.palette.grey[300]
    const pointerColor = isDarkTheme ? theme.palette.common.white : theme.palette.common.black
    const successColor = isUp ? '#00897B' : '#ff5252'

    return (
        <Box display="flex" flexDirection="column" gap={3} width="100%" py={0} borderRadius={1}>
            {/* Day's Range */}
            <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" color="text.primary">
                        {formatNumber(dayLow)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Day's Range
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                        {formatNumber(dayHigh)}
                    </Typography>
                </Box>
                <Box position="relative" height={6} bgcolor={barBgColor} borderRadius={3} mb={2}>
                    {/* Segment between open and LTP */}
                    {Math.abs(ltpPosDay - openPosDay) > 0.5 &&
                        (() => {
                            const leftPos = Math.min(openPosDay, ltpPosDay)
                            const rightPos = Math.max(openPosDay, ltpPosDay)
                            const leftRadius = leftPos <= 0.1 ? '3px' : '0'
                            const rightRadius = rightPos >= 99.9 ? '3px' : '0'
                            const borderRadius = `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`

                            return <Box position="absolute" left={`${leftPos}%`} width={`${rightPos - leftPos}%`} height="100%" bgcolor={successColor} sx={{ borderRadius }} />
                        })()}

                    {/* Pointer */}
                    {shouldShowDayPointer && (
                        <UpArrow
                            sx={{
                                position: 'absolute',
                                left: `${ltpPosDay}%`,
                                top: 6,
                                fontSize: 12,
                                color: pointerColor,
                                transform: 'translateX(-50%)'
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* 52 Week Range */}
            {show52WkRange && (
                <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.primary">
                            {formatNumber(weekLow)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            52 Week Range
                        </Typography>
                        <Typography variant="caption" color="text.primary">
                            {formatNumber(weekHigh)}
                        </Typography>
                    </Box>
                    <Box position="relative" height={6} bgcolor={barBgColor} borderRadius={3} mb={2}>
                        {/* Segment between Day Low and Day High inside 52W */}
                        {(() => {
                            const leftPos = Math.min(dayLowPosWeek, dayHighPosWeek)
                            const rightPos = Math.max(dayLowPosWeek, dayHighPosWeek)
                            const leftRadius = leftPos <= 0.1 ? '3px' : '0'
                            const rightRadius = rightPos >= 99.9 ? '3px' : '0'
                            const borderRadius = `${leftRadius} ${rightRadius} ${rightRadius} ${leftRadius}`
                            return (
                                <Box
                                    position="absolute"
                                    left={`${leftPos}%`}
                                    width={`${Math.max(rightPos - leftPos, 0.5)}%`}
                                    height="100%"
                                    bgcolor={successColor}
                                    sx={{ borderRadius, minWidth: leftPos === rightPos ? '2px' : 'auto' }}
                                />
                            )
                        })()}
                        {/* Pointer */}
                        {shouldShowWeekPointer && (
                            <UpArrow
                                sx={{
                                    position: 'absolute',
                                    left: `${ltpPosWeek}%`,
                                    top: 6,
                                    fontSize: 12,
                                    color: pointerColor,
                                    transform: 'translateX(-50%)'
                                }}
                            />
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export default RangeBar
